import { createContext, ReactElement, useEffect, useReducer } from 'react';

// third-party
import { Chance } from 'chance';
import { jwtDecode } from 'jwt-decode';

// reducer - state management
import { LOGIN, LOGOUT } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';

// project-imports
import Loader from 'components/Loader';
import axios from 'utils/axios';

// types
import { AuthProps, JWTContextType } from 'types/auth';
import { KeyedObject } from 'types/root';

const chance = new Chance();
const isDevAuthBypassEnabled = import.meta.env.VITE_APP_ENABLE_DEV_AUTH_BYPASS === 'true';

// constant
const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

const createDevAuthUser = (email = 'dev@sentinel.local') => ({
  id: 0,
  email,
  name: 'Sentinel Dev Admin',
  role: 'super_admin' as const,
  tenantId: null,
  tenantName: null,
  disabled: false,
  tier: 'development'
});

const verifyToken: (st: string) => boolean = (serviceToken) => {
  if (!serviceToken) {
    return false;
  }
  const decoded: KeyedObject = jwtDecode(serviceToken);
  /**
   * Property 'exp' does not exist on type '<T = unknown>(token: string, options?: JwtDecodeOptions | undefined) => T'.
   */
  return decoded.exp > Date.now() / 1000;
};

const setSession = (serviceToken?: string | null) => {
  if (serviceToken) {
    localStorage.setItem('serviceToken', serviceToken);
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  } else {
    localStorage.removeItem('serviceToken');
    delete axios.defaults.headers.common.Authorization;
  }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider = ({ children }: { children: ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const refreshUser = async () => {
    if (isDevAuthBypassEnabled) {
      const user = createDevAuthUser();
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user
        }
      });
      return user;
    }

    const response = await axios.get('/auth/me');
    const user = response.data.user;
    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user
      }
    });
    return user;
  };

  useEffect(() => {
    const init = async () => {
      try {
        if (isDevAuthBypassEnabled) {
          setSession(null);
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user: createDevAuthUser()
            }
          });
          return;
        }

        const serviceToken = window.localStorage.getItem('serviceToken');
        if (serviceToken && verifyToken(serviceToken)) {
          setSession(serviceToken);
          await refreshUser();
        } else {
          dispatch({
            type: LOGOUT
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: LOGOUT
        });
      }
    };

    init();
  }, []);

  const login = async (email: string, password: string) => {
    if (isDevAuthBypassEnabled) {
      void password;
      setSession(null);
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: createDevAuthUser(email)
        }
      });
      return;
    }

    const response = await axios.post('/auth/login', { email, password });
    const { serviceToken, user } = response.data;
    setSession(serviceToken);
    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user
      }
    });
  };

  const register = async (email: string, password: string, firstName: string, lastName: string) => {
    // todo: this flow need to be recode as it not verified
    const id = chance.bb_pin();
    const response = await axios.post('/api/account/register', {
      id,
      email,
      password,
      firstName,
      lastName
    });
    let users = response.data;

    if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
      const localUsers = window.localStorage.getItem('users');
      users = [
        ...JSON.parse(localUsers!),
        {
          id,
          email,
          password,
          name: `${firstName} ${lastName}`
        }
      ];
    }

    window.localStorage.setItem('users', JSON.stringify(users));
  };

  const logout = () => {
    if (isDevAuthBypassEnabled) {
      setSession(null);
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: createDevAuthUser()
        }
      });
      return;
    }

    setSession(null);
    dispatch({ type: LOGOUT });
  };

  const resetPassword = async (email: string) => {
    console.log('email - ', email);
  };

  const updateProfile = async (input: { name: string; email: string }) => {
    if (isDevAuthBypassEnabled) {
      const user = {
        ...createDevAuthUser(input.email),
        name: input.name
      };
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user
        }
      });
      return user;
    }

    const response = await axios.put('/auth/me', input);
    const user = response.data.item;
    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user
      }
    });
    return user;
  };

  const updatePassword = async (currentPassword: string, newPassword: string) => {
    if (isDevAuthBypassEnabled) {
      return;
    }

    await axios.put('/auth/me/password', {
      currentPassword,
      newPassword
    });
  };

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext
      value={{ ...state, login, logout, register, resetPassword, updateProfile, updatePassword, refreshUser }}
    >
      {children}
    </JWTContext>
  );
};

export default JWTContext;
